import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faSearch, faChevronDown, faPlay, faCirclePlay } from '@fortawesome/free-solid-svg-icons';

import api, { SERVER_URL } from '../../utils/api';
import { useRecentSearches } from '../../components/RecentSearches';
import '../../styles/help.css';

/**
 * Help & Training — the onboarding videos, the videos for each role, and an FAQ
 * built on them.
 *
 * Everything on the page comes from /api/training/content, which returns only
 * what the signed-in user's role may see (see server/routes/training.js): the
 * employee videos for everyone, and the Team Lead / Manager / HR / Accounts /
 * Admin videos for those roles. Admin sees every video, with tags saying who
 * each one is for. The video files are served only with a short-lived signature
 * that is tied to the viewer's role.
 *
 * The language switch changes the videos only; question and answer text is
 * English on purpose.
 */
const LANG_KEY = 'help.lang';
const LANGS = [{ id: 'en', label: 'English' }, { id: 'hi', label: 'हिन्दी' }];

const readLang = () => {
    try { return localStorage.getItem(LANG_KEY) === 'hi' ? 'hi' : 'en'; } catch { return 'en'; }
};
const fmt = (s) => {
    const t = Math.max(0, Math.floor(s || 0));
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

// Which step of a video a time falls in, and where that step starts in the other language.
const mapTime = (video, t, from, to) => {
    const a = video.steps?.[from], b = video.steps?.[to];
    if (!a || !b) return 0;
    let i = 0;
    while (i + 1 < a.length && a[i + 1] <= t) i++;
    return Math.max(0, b[i] + Math.min(t - a[i], 2));
};

// The page content for this user, plus the signed query string for the media route
// (refreshed before it expires).
const useHelpContent = () => {
    const [state, setState] = useState({ loading: true, available: false, query: '', isAdmin: false, videos: [], groups: [], faqs: [] });
    useEffect(() => {
        let timer, alive = true;
        const schedule = (expiresAt) => {
            clearTimeout(timer);
            if (expiresAt) timer = setTimeout(refresh, Math.max(60000, expiresAt - Date.now() - 10 * 60000));
        };
        const refresh = async () => {
            try {
                const { data } = await api.get('/training/access');
                if (!alive) return;
                setState(s => ({ ...s, available: !!data.available, query: data.query || '' }));
                schedule(data.expiresAt);
            } catch { /* keep the current links; they still work until they expire */ }
        };
        (async () => {
            try {
                const { data } = await api.get('/training/content');
                if (!alive) return;
                setState({ loading: false, available: !!data.available, query: data.query || '', isAdmin: !!data.isAdmin,
                    videos: data.videos || [], groups: data.groups || [], faqs: data.faqs || [] });
                schedule(data.expiresAt);
            } catch {
                if (alive) setState(s => ({ ...s, loading: false, available: false }));
            }
        })();
        return () => { alive = false; clearTimeout(timer); };
    }, []);
    const url = useCallback((file) => `${SERVER_URL}/api/training/media/${file}?${state.query}`, [state.query]);
    return { ...state, url };
};

const Tags = ({ tags }) => (tags && tags.length ? (
    <span className="ht-tags">{tags.map((t) => <span key={t} className="ht-tag">{t}</span>)}</span>
) : null);

/* ------------------------------------------------------------------ full video (every visible video back to back) */
const FullPlayer = ({ videos, lang, media, request }) => {
    const videoRef = useRef(null);
    const pending = useRef(null);          // { t, play } applied once the new source has loaded
    const [index, setIndex] = useState(0);
    const [now, setNow] = useState(0);
    const current = videos[index];
    const prevLang = useRef(lang);

    // chapter start times on the combined timeline, per language
    const starts = useMemo(() => {
        let t = 0;
        return videos.map((v) => { const s = t; t += v.duration?.[lang] || 0; return s; });
    }, [videos, lang]);

    const load = useCallback((i, t = 0, play = true) => {
        pending.current = { t, play };
        setIndex(i);
        const el = videoRef.current;
        if (el && i === index) { el.currentTime = t; if (play) el.play().catch(() => {}); pending.current = null; }
    }, [index]);

    // language switch: same chapter, same step. Decided while rendering: the old
    // element is still playing (removing it pauses it), and the new one can load
    // its metadata before any effect would run.
    const last = useRef({ t: 0, playing: false });
    if (current && prevLang.current !== lang) {
        pending.current = { t: mapTime(current, last.current.t, prevLang.current, lang), play: last.current.playing };
        prevLang.current = lang;
    }

    // a question asked to open a place in the full video
    useEffect(() => {
        if (request) load(request.index, request.t, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request]);

    if (!current) return null;

    const onLoaded = () => {
        const el = videoRef.current, p = pending.current;
        if (!el || !p) return;
        pending.current = null;
        el.currentTime = p.t;
        if (p.play) el.play().catch(() => {});
    };
    const onEnded = () => { if (index + 1 < videos.length) load(index + 1, 0, true); };
    const lastVideo = videos[videos.length - 1];

    return (
        <div className="ht-full">
            <div className="ht-full-player">
                {media.available ? (
                    <video
                        ref={videoRef}
                        key={`${current.file}-${lang}`}
                        className="ht-video"
                        controls
                        playsInline
                        preload="metadata"
                        poster={media.url(`${current.file}-${lang}.jpg`)}
                        src={media.url(`${current.file}-${lang}.mp4`)}
                        onLoadedMetadata={onLoaded}
                        onTimeUpdate={(e) => { const t = e.currentTarget.currentTime; last.current.t = t; setNow(t); }}
                        onPlay={() => { last.current.playing = true; }}
                        onPause={() => { last.current.playing = false; }}
                        onEnded={onEnded}
                    />
                ) : (
                    <div className="ht-video ht-video-off">{media.loading ? 'Loading videos…' : 'The videos are not available right now.'}</div>
                )}
                <div className="ht-now">
                    <span className="ht-now-n">Chapter {index + 1} of {videos.length}</span>
                    <strong>{current.title}</strong>
                    <Tags tags={current.tags} />
                    <span className="ht-now-t">{fmt(starts[index] + now)} / {fmt(starts[videos.length - 1] + (lastVideo.duration?.[lang] || 0))}</span>
                </div>
            </div>
            <ol className="ht-chapters" aria-label="Chapters">
                {videos.map((v, i) => (
                    <li key={v.n}>
                        <button type="button" className={`ht-chapter ${i === index ? 'is-current' : ''}`}
                            onClick={() => load(i, 0, true)} disabled={!media.available}>
                            <span className="ht-chapter-t">{fmt(starts[i])}</span>
                            <span className="ht-chapter-name">
                                {v.label}. {v.title}
                                <Tags tags={v.tags} />
                            </span>
                            {i === index && <FontAwesomeIcon icon={faPlay} className="ht-chapter-icon" />}
                        </button>
                    </li>
                ))}
            </ol>
        </div>
    );
};

/* ------------------------------------------------------------------ one question */
const Question = ({ faq, video, lang, media, open, onToggle, onFull }) => {
    const ref = useRef(null);
    const start = faq.start?.[lang] || 0;
    // start at the step the question is about, again after a language switch
    useEffect(() => { const el = ref.current; if (el && open) el.currentTime = start; }, [start, open]);
    return (
        <div className={`ht-q ${open ? 'is-open' : ''}`}>
            <button type="button" className="ht-q-head" aria-expanded={open} onClick={onToggle}>
                <span>{faq.q}</span>
                <FontAwesomeIcon icon={faChevronDown} className="ht-q-chevron" />
            </button>
            {open && (
                <div className="ht-q-body">
                    <p className="ht-q-answer">{faq.a}</p>
                    {media.available && (
                        <>
                            <video
                                ref={ref}
                                key={`${video.file}-${lang}`}
                                className="ht-video"
                                controls
                                playsInline
                                preload="metadata"
                                src={`${media.url(`${video.file}-${lang}.mp4`)}#t=${start}`}
                                onLoadedMetadata={(e) => { e.currentTarget.currentTime = start; }}
                            />
                            <div className="ht-q-foot">
                                <span>From video {video.label}: {video.title} <Tags tags={video.tags} /></span>
                                <button type="button" className="ht-link" onClick={onFull}>
                                    <FontAwesomeIcon icon={faCirclePlay} /> Watch in the full video
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ page */
const HelpTraining = () => {
    const [lang, setLang] = useState(readLang);
    const [search, setSearch] = useState('');
    const [openId, setOpenId] = useState(null);
    const recentSearch = useRecentSearches('help', { value: search, onPick: setSearch });
    const [request, setRequest] = useState(null);
    const content = useHelpContent();
    const topRef = useRef(null);

    const chooseLang = (l) => {
        setLang(l);
        try { localStorage.setItem(LANG_KEY, l); } catch { /* not remembered, still switches */ }
    };

    const videoByN = useMemo(() => Object.fromEntries(content.videos.map((v) => [v.n, v])), [content.videos]);
    const indexByN = useMemo(() => Object.fromEntries(content.videos.map((v, i) => [v.n, i])), [content.videos]);
    const q = search.trim().toLowerCase();
    const groups = useMemo(() => content.groups.map((g) => ({
        ...g,
        faqs: content.faqs.filter((f) => g.videos.includes(f.video) && videoByN[f.video]
            && (!q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))),
    })).filter((g) => g.faqs.length), [content.groups, content.faqs, videoByN, q]);

    const openInFull = (faq) => {
        recentSearch.save();
        setRequest({ index: indexByN[faq.video] ?? 0, t: faq.start?.[lang] || 0, at: Date.now() });
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="settings-container fade-in ht-page" ref={topRef}>
            <div className="page-header-row mb-20 ht-header">
                <div>
                    <h1 className="page-title header-no-margin">
                        <FontAwesomeIcon icon={faCircleQuestion} className="btn-icon" /> Help &amp; Training
                    </h1>
                    <p className="ht-sub">Short videos on every part of the portal, and answers to common questions.</p>
                </div>
                <div className="ht-lang" role="group" aria-label="Video language">
                    <span className="ht-lang-label">Video language</span>
                    {LANGS.map((l) => (
                        <button key={l.id} type="button" className={lang === l.id ? 'is-active' : ''}
                            aria-pressed={lang === l.id} onClick={() => chooseLang(l.id)}>{l.label}</button>
                    ))}
                </div>
            </div>

            <section className="ht-card">
                <h2 className="ht-h2">Complete training video</h2>
                <p className="ht-hint">
                    {content.loading ? 'Loading…'
                        : content.videos.length ? `All ${content.videos.length} videos for you, in one. Click a chapter to jump to it.`
                        : 'Help content could not be loaded. Please refresh the page.'}
                    {content.isAdmin && ' Tags show who each video is for.'}
                </p>
                {content.videos.length > 0 && <FullPlayer videos={content.videos} lang={lang} media={content} request={request} />}
            </section>

            <section className="ht-card">
                <div className="ht-faq-head">
                    <h2 className="ht-h2">Frequently asked questions</h2>
                    <div className="search-wrapper ht-search">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input {...recentSearch.inputProps} type="text" className="swal2-input search-input" placeholder="Search questions…"
                            value={search} onChange={(e) => setSearch(e.target.value)} />
                        {recentSearch.menu}
                    </div>
                </div>
                {!content.loading && groups.length === 0 && <div className="ht-empty">{q ? `No questions match “${search}”.` : 'No questions yet.'}</div>}
                {groups.map((g) => (
                    <div key={g.id} className="ht-group">
                        <h3 className="ht-group-title">{g.title}</h3>
                        {g.faqs.map((f) => (
                            <Question key={f.id} faq={f} video={videoByN[f.video]} lang={lang} media={content}
                                open={openId === f.id} onToggle={() => { if (openId !== f.id) recentSearch.save(); setOpenId(openId === f.id ? null : f.id); }}
                                onFull={() => openInFull(f)} />
                        ))}
                    </div>
                ))}
            </section>
        </div>
    );
};

export default HelpTraining;
